import {
  BRAND_BLUESKY,
  BRAND_FACEBOOK,
  BRAND_GOOGLE,
  BRAND_INSTAGRAM,
  BRAND_LINKEDIN,
  BRAND_PINTEREST,
  BRAND_THREADS,
  BRAND_TIKTOK,
  BRAND_TWITTER,
  BRAND_YOUTUBE,
  POST_TYPE_ARTICLE_LINK,
  POST_TYPE_PHOTO_LINK,
  POST_TYPE_VIDEO_LINK,
  REPEAT_PERIOD_DAYS,
  REPEAT_PERIOD_MONTHS,
  REPEAT_PERIOD_WEEKS
} from '../../constants'
import { ContentFeedType } from '../Content'
import { PostDestination } from '../User'
import { PostComment } from './common'
import { ComposerBasePost } from './ComposerBasePost'
import { ComposerBlueskyPost } from './ComposerBlueskyPost'
import { BackgroundTemplate, ComposerFacebookPost } from './ComposerFacebookPost'
import { ComposerGooglePost, GooglePostButtonType } from './ComposerGooglePost'
import { ComposerInstagramPost, LocationInfo } from './ComposerInstagramPost'
import { ComposerLinkedinPost } from './ComposerLinkedinPost'
import { ComposerPinterestPost, PinterestAiContentMode } from './ComposerPinterestPost'
import { ComposerThreadsPost } from './ComposerThreadsPost'
import { ComposerTikTokPost, TikTokPostProfileSettings } from './ComposerTikTokPost'
import { ComposerTwitterPost } from './ComposerTwitterPost'
import { ComposerYoutubePost, PrivacyStatus } from './ComposerYoutubePost'

export * from './ComposerFacebookPost'
export * from './ComposerGooglePost'
export * from './ComposerInstagramPost'
export * from './ComposerPinterestPost'
export * from './ComposerTikTokPost'
export * from './ComposerTwitterPost'
export * from './ComposerYoutubePost'
export * from './writer'
export type { ComposerBasePost, ComposerBlueskyPost, ComposerLinkedinPost, ComposerThreadsPost }

export interface LinkDetails {
  description: string
  domain: string
  image: string[]
  images: string[]
  selectedImageUrl?: string
  title: string
  url: string
  type: typeof POST_TYPE_PHOTO_LINK | typeof POST_TYPE_VIDEO_LINK | typeof POST_TYPE_ARTICLE_LINK
  selected?: boolean
}

export type RepeatPeriod = typeof REPEAT_PERIOD_DAYS
  | typeof REPEAT_PERIOD_MONTHS
  | typeof REPEAT_PERIOD_WEEKS

export interface RepeatInfo {
  willRepeat: boolean
  length: number
  period: RepeatPeriod
  until: 'forever' | Date
}

export interface PostScheduleInfo {
  postDate: string
  repeat: boolean
  repeatLength?: number
  repeatPeriod?: RepeatPeriod
  repeatUntil?: string
}

export interface SocialProfileTag {
  pageId: string
  name: string
  handle: string
  imageUrl: string
  url: string
}

export interface CreatePostRequestData {
  frontendId?: string
  sourceFeature?: string
  postType: 'status' | 'link' | 'gif' | 'photo' | 'video' | 'bulk' | 'video_link'
  shareType?: 'now' | 'next' | 'queue'
  ppPageIds: number[]
  recycle?: boolean
  recycleUntil?: string
  recycleTimes?: number
  link?: {
    title: string
    url: string
    description?: string
  }
  photos?: string[]
  featuredImageUrl?: string
  altText?: string
  videoUrl?: string
  videoThumbnail?: string
  videoThumbnailOffset?: number
  videoThumbnailOffsetUrl?: string
  videoThumbnailUrl?: string
  bulkUploadFileUrl?: string
  schedule?: PostScheduleInfo
  networks: {
    facebook?: {
      status?: string
      reshare?: boolean
      comments?: PostComment[]
      details?: {
        albums?: Array<{ id: string, name: string, ppPageId: string }>
        targeting?: any[]
        tags?: SocialProfileTag[]
        reshareUrl?: string
        postType?: string
        isAiContent?: boolean
      }
    }
    twitter?: {
      status?: string
      comments?: PostComment[]
      details: {
        tweetType: 'new' | 'retweet' | 'quote'
        tweetUrl?: string // original tweet url for retweets/quote-tweets
        tags?: SocialProfileTag[]
        isAiContent?: boolean
      }
    }
    bluesky?: {
      status?: string
      comments?: PostComment[]
      details?: {
        facets?: any
        tags?: SocialProfileTag[]
      }
    }
    instagram?: {
      status?: string
      comments?: PostComment[]
      details?: {
        locations?: Array<LocationInfo & { ppPageId: string }>
        coverUrl?: string
        isAiContent?: boolean
      }
    }
    pinterest?: {
      status?: string
      description?: string
      destinationUrl?: string
      /** `null` clears a previously stored mode; omitting the key on this full-replace endpoint would too. */
      piAiContentMode?: PinterestAiContentMode | null
      details: {
        boards: Array<{ id: string, name: string, ppPageId: string }>
        isAiContent?: boolean
      }
    }
    linkedin?: {
      status?: string
      comments?: PostComment[]
      details: {
        tags?: SocialProfileTag[]
        text?: string
        documentUrl?: string
        documentName?: string
      }
    },
    google?: {
      status?: string
      details?: {
        postType?: string
        buttonType?: GooglePostButtonType
        buttonUrl?: string
        couponCode?: string
        redeemOnlineUrl?: string
        termsConditions?: string
        title?: string
        startDate?: string
        endDate?: string
      }
    },
    tiktok?: {
      status?: string
      title?: string
      description?: string
      details?: {
        settings: TikTokPostProfileSettings[]
      }
    },
    youtube?: {
      status?: string
      description?: string
      comments?: PostComment[]
      details: {
        privacyStatus: PrivacyStatus
        isAiContent?: boolean
      }
    },
    threads?: {
      status?: string
      comments?: PostComment[]
    },
  }
  sourcePost?: {
    text?: string
    date?: string
    feedName?: string
    feedType?: ContentFeedType
    socialLink?: string
    sourceLink?: string
  }
}

export type PostDraftData<CS> = Omit<CreatePostRequestData, 'shareType'> & {
  id: string
  updatedAt: string
  state: Partial<CS>
  profiles: PostDestination[]
}

export interface ComposerScheduleState {
  isActive: boolean
  time: string
  date: string
  repeat: boolean
  repeatCount: number
  repeatPeriod: RepeatPeriod
  repeatUntil: 'date' | 'forever'
  repeatUntilDate: string
}

export const COMPOSER_STATUS_KEY_GENERIC = 'generic'
export interface ComposerStateShared {
  posts: {
    [BRAND_FACEBOOK]: ComposerFacebookPost
    [BRAND_TWITTER]: ComposerTwitterPost
    [BRAND_PINTEREST]: ComposerPinterestPost
    [BRAND_INSTAGRAM]: ComposerInstagramPost
    [BRAND_LINKEDIN]: ComposerLinkedinPost
    [BRAND_TIKTOK]: ComposerTikTokPost
    [BRAND_GOOGLE]: ComposerGooglePost
    [BRAND_YOUTUBE]: ComposerYoutubePost
    [BRAND_THREADS]: ComposerThreadsPost
    [BRAND_BLUESKY]: ComposerBlueskyPost
    [COMPOSER_STATUS_KEY_GENERIC]: ComposerBasePost
  }
  useGenericStatus: boolean
  draft?: {
    id: string
    updatedAt: string
  }
  featuredImageUrl?: string
  videoUrl?: string
  videoDuration?: number
  videoSize?: number
  videoThumbnailUrl?: string
  videoThumbnailOffset?: number
  videoMetadata?: {
    width?: number
    height?: number
    fileType?: string
  }
  postAsPhoto: boolean
  recycle: boolean | Date | number
  links: LinkDetails[]
  pinterestBoards: { [ppid: string]: Array<{ id: string, name: string, url?: string, selected?: boolean }> }
  schedule: ComposerScheduleState
  facebookBackgroundTemplates: BackgroundTemplate[]
  queuePostAction: 'top' | 'bottom'
  selectedProfiles: string[]
  rememberIGLocations: boolean
  postEdit?: Readonly<{
    id: string
    ppid: string
    bucketId?: string
  }>
}
